import React, { useState } from 'react';

// Boutons/checklist alignés sur la palette déjà utilisée dans UsageCategoryPicker.js
const primaryButtonStyle = {
    padding: '10px 18px', borderRadius: '6px', border: 'none',
    background: '#2563eb', color: '#fff', fontWeight: 600, fontSize: '0.88em',
    cursor: 'pointer', font: 'inherit',
};

const secondaryButtonStyle = {
    padding: '10px 18px', borderRadius: '6px', border: '1px solid #d1d5db',
    background: '#fff', color: '#374151', fontWeight: 500, fontSize: '0.88em',
    cursor: 'pointer', font: 'inherit',
};

const ghostButtonStyle = {
    padding: '10px 18px', borderRadius: '6px', border: 'none',
    background: 'transparent', color: '#6b7280', fontWeight: 500, fontSize: '0.85em',
    cursor: 'pointer', font: 'inherit', textDecoration: 'underline',
};

const linkButtonStyle = {
    border: 'none', background: 'none', color: '#2563eb', fontSize: '0.8em',
    fontWeight: 600, cursor: 'pointer', font: 'inherit', padding: 0,
};

const rowStyle = {
    display: 'flex', alignItems: 'center', gap: '8px',
    padding: '6px 0', fontSize: '0.88em', color: '#374151',
};

const buildInitialSelection = (groups, defaultAllChecked) => {
    const selection = {};
    groups.forEach((group) => {
        selection[group.key] = new Set(
            group.items
                .filter((item) => defaultAllChecked || item.matchesOld)
                .map((item) => item._id)
        );
    });
    return selection;
};

/**
 * Modale de cascade pour les unités fonctionnelles — réutilisée pour :
 * - la suppression d'une unité (targetOptions fourni : l'utilisateur choisit une unité de
 *   remplacement, ou de vider)
 * - la modification de l'unité d'une section/pièce (fixedTargetLabel fourni : la cible est
 *   déjà connue, seule la sélection des éléments à propager reste à faire)
 */
export default function FunctionalUnitCascadeModal({
    title,
    description,
    groups, // [{ key, label, items: [{_id, label, matchesOld}] }]
    targetOptions, // [{id, label}] | undefined
    fixedTargetLabel, // string | undefined
    defaultAllChecked = false,
    onConfirm, // (selection: {[key]: string[]}, targetUnitId: string|null) => void
    onSkip, // () => void | undefined — "enregistrer sans propager"
    onCancel,
}) {
    const [selection, setSelection] = useState(() => buildInitialSelection(groups, defaultAllChecked));
    const [targetUnitId, setTargetUnitId] = useState(targetOptions?.[0]?.id ?? '');

    const toggleItem = (groupKey, itemId) => {
        setSelection((prev) => {
            const next = new Set(prev[groupKey]);
            if (next.has(itemId)) next.delete(itemId);
            else next.add(itemId);
            return { ...prev, [groupKey]: next };
        });
    };

    const selectAll = (groupKey, allSelected) => {
        setSelection((prev) => {
            const group = groups.find((g) => g.key === groupKey);
            const next = allSelected ? new Set() : new Set(group.items.map((item) => item._id));
            return { ...prev, [groupKey]: next };
        });
    };

    const totalSelected = Object.values(selection).reduce((sum, set) => sum + set.size, 0);

    const handleConfirm = () => {
        const selectionAsArrays = Object.fromEntries(
            Object.entries(selection).map(([key, set]) => [key, Array.from(set)])
        );
        onConfirm(selectionAsArrays, targetOptions ? (targetUnitId || null) : null);
    };

    return (
        <div className="section-popup">
            <div className="section-popup-content" style={{ maxWidth: '560px' }}>
                <h2>{title}</h2>
                {description && (
                    <p style={{ fontSize: '0.88em', color: '#6b7280', marginTop: '-12px', marginBottom: '18px' }}>
                        {description}
                    </p>
                )}

                {targetOptions && (
                    <div style={{ marginBottom: '18px' }}>
                        <label style={{ display: 'block', fontSize: '0.85em', fontWeight: 600, marginBottom: '6px' }}>
                            Réaffecter vers
                        </label>
                        <select
                            value={targetUnitId}
                            onChange={(e) => setTargetUnitId(e.target.value)}
                            style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                        >
                            {targetOptions.map((opt) => (
                                <option key={opt.id || 'clear'} value={opt.id}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                )}

                {fixedTargetLabel && (
                    <p style={{ fontSize: '0.88em', marginBottom: '18px' }}>
                        Nouvelle unité fonctionnelle : <strong>{fixedTargetLabel}</strong>
                    </p>
                )}

                {groups.map((group) => {
                    if (group.items.length === 0) return null;
                    const groupSelection = selection[group.key];
                    const allSelected = groupSelection.size === group.items.length;
                    return (
                        <div key={group.key} style={{ marginBottom: '16px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                <span style={{ fontSize: '0.85em', fontWeight: 600 }}>
                                    {group.label} ({groupSelection.size}/{group.items.length})
                                </span>
                                <button type="button" style={linkButtonStyle} onClick={() => selectAll(group.key, allSelected)}>
                                    {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
                                </button>
                            </div>
                            <div style={{ maxHeight: '160px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '4px 10px' }}>
                                {group.items.map((item) => (
                                    <label key={item._id} style={rowStyle}>
                                        <input
                                            type="checkbox"
                                            checked={groupSelection.has(item._id)}
                                            onChange={() => toggleItem(group.key, item._id)}
                                        />
                                        <span>{item.label}</span>
                                        {!item.matchesOld && (
                                            <span style={{ fontSize: '0.78em', color: '#b45309' }}>(unité différente)</span>
                                        )}
                                    </label>
                                ))}
                            </div>
                        </div>
                    );
                })}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '20px' }}>
                    <button type="button" style={primaryButtonStyle} onClick={handleConfirm}>
                        {totalSelected > 0 ? `Enregistrer et propager (${totalSelected})` : 'Enregistrer'}
                    </button>
                    {onSkip && (
                        <button type="button" style={secondaryButtonStyle} onClick={onSkip}>
                            Enregistrer sans propager
                        </button>
                    )}
                    <button type="button" style={ghostButtonStyle} onClick={onCancel}>
                        Annuler
                    </button>
                </div>
            </div>
        </div>
    );
}
